import { useMemo, useState } from 'react';

import { Select, Spin } from 'antd';

import IntlMessages from '@src/util/IntlMessages';

import type {
  ElasticPaginatedSelectOption,
  ElasticPaginatedSelectProps,
} from './ElasticPaginatedSelect.interfaces';
import { shouldCommitElasticSelectOnChange } from './shouldCommitElasticSelectOnChange';
import { useElasticPaginatedSelect } from './useElasticPaginatedSelect';
import styles from './styles.module.less';

const LOADING_OPTION_VALUE = '__elastic_paginated_select_loading__';
const SCROLL_THRESHOLD_PX = 20;

/** Ant Design only shows placeholder when value is undefined (not ''). */
const toSelectValue = (value: string | string[] | undefined) => {
  if (value == null) return undefined;
  if (typeof value === 'string') return value === '' ? undefined : value;
  if (value.length === 0) return undefined;
  return value;
};

const DropdownLoading = () => (
  <div className={styles.dropdownLoading} aria-busy="true" aria-live="polite">
    <Spin size="small" />
    <span className={styles.dropdownLoadingText}>
      <IntlMessages id="general.loading.options" />
    </span>
  </div>
);

/**
 * Async, paginated select with debounced prefix search backed by Elasticsearch.
 * Supports single selection (default) and multiple selection (`mode="multiple"`).
 * Preselected values are resolved internally when they are not in the current page.
 * Successor to the legacy `DebounceCloudFetchSelect`.
 */
function ElasticPaginatedSelect<TSource = Record<string, unknown>>({
  value,
  onChange,
  placeholder = 'Buscar...',
  emptyPlaceholder = 'general.empty.state',
  errorPlaceholder = 'general.loading.options.error',
  disabled = false,
  id,
  ariaLabel,
  className = 'gx-w-100',
  listHeight = 200,
  mode,
  maxTagCount,
  maxTagTextLength,
  optionLabelProp,
  allowClear = true,
  onClear,
  getPopupContainer = trigger => trigger.parentNode as HTMLElement,
  ...queryParams
}: ElasticPaginatedSelectProps<TSource>) {
  const [searchValue, setSearchValue] = useState('');
  const { options, loading, loadingMore, error, onSearch, onLoadMore, reset, registerSelection } =
    useElasticPaginatedSelect<TSource>({
      ...queryParams,
      value,
      mode,
    });

  const displayOptions = useMemo<ElasticPaginatedSelectOption[]>(() => {
    if (!loadingMore) return options;

    return [
      ...options,
      {
        label: (
          <div className={styles.loadingMore}>
            <Spin size="small" />
          </div>
        ),
        value: LOADING_OPTION_VALUE,
        disabled: true,
      },
    ];
  }, [loadingMore, options]);

  const handleSearch = (text: string) => {
    setSearchValue(text);
    onSearch(text);
  };

  const applyChange = (
    newValue: string | string[] | undefined,
    option: ElasticPaginatedSelectOption | ElasticPaginatedSelectOption[] | undefined,
  ) => {
    setSearchValue('');
    if (option) {
      const selectedOptions = Array.isArray(option) ? option : [option];
      selectedOptions.forEach(registerSelection);
    }
    onChange?.(newValue, option);
  };

  const handlePopupScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (loading || loadingMore) return;

    const target = event.target as HTMLElement;
    const reachedBottom =
      target.scrollTop + target.offsetHeight >= target.scrollHeight - SCROLL_THRESHOLD_PX;
    if (!reachedBottom) return;

    const currentSearch = target.closest('.ant-select')?.querySelector('input')?.value ?? '';
    onLoadMore(currentSearch);
  };

  return (
    <Select
      id={id}
      aria-label={ariaLabel}
      mode={mode}
      maxTagCount={maxTagCount}
      maxTagTextLength={maxTagTextLength}
      optionLabelProp={optionLabelProp}
      getPopupContainer={getPopupContainer}
      disabled={disabled}
      showSearch
      allowClear={allowClear}
      onClear={() => {
        setSearchValue('');
        (onClear ?? reset)();
      }}
      placeholder={placeholder}
      filterOption={false}
      options={displayOptions}
      loading={loading}
      className={className}
      searchValue={searchValue}
      onSearch={handleSearch}
      notFoundContent={
        loading ? (
          <DropdownLoading />
        ) : (
          <IntlMessages id={error ? errorPlaceholder : emptyPlaceholder} />
        )
      }
      value={toSelectValue(value)}
      listHeight={listHeight}
      onPopupScroll={handlePopupScroll}
      onChange={(newValue, option) => {
        if (!shouldCommitElasticSelectOnChange(mode, newValue)) return;
        applyChange(newValue, option as ElasticPaginatedSelectOption | ElasticPaginatedSelectOption[]);
      }}
      onSelect={(selectedValue, option) => {
        // Controlled search: single-select commits here (antd also fires onChange;
        // that path is skipped via shouldCommitElasticSelectOnChange).
        if (mode) return;
        applyChange(selectedValue as string, option as ElasticPaginatedSelectOption);
      }}
      onDropdownVisibleChange={open => {
        if (!open) setSearchValue('');
      }}
    />
  );
}

export default ElasticPaginatedSelect;
